import { StyleType, CssColor, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import { StyleParamType, wixColorParam, wixFontParam, IWixStyleFont, createStylesParams } from '@wix/tpa-settings';
import { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { Alignment, alignmentName } from '../../utils/alignment';

export type IStylesParams = {
  contentAlignment: StyleParamType.Number;
  backgroundColor: StyleParamType.Color;
  borderColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderTopLeftRadius: StyleParamType.Number;
  borderTopRightRadius: StyleParamType.Number;
  borderBottomRightRadius: StyleParamType.Number;
  borderBottomLeftRadius: StyleParamType.Number;
  titleFont: StyleParamType.Font;
  titleMobileFontSize: StyleParamType.Number;
  titleColor: StyleParamType.Color;
  buttonFont: StyleParamType.Font;
  buttonFontColor: StyleParamType.Color;
  buttonBackgroundColor: StyleParamType.Color;
  buttonBorderColor: StyleParamType.Color;
  buttonBorderWidth: StyleParamType.Number;
  buttonBorderTopLeftRadius: StyleParamType.Number;
  buttonBorderTopRightRadius: StyleParamType.Number;
  buttonBorderBottomRightRadius: StyleParamType.Number;
  buttonBorderBottomLeftRadius: StyleParamType.Number;
  buttonFontColorHover: StyleParamType.Color;
  buttonBackgroundColorHover: StyleParamType.Color;
  buttonBorderColorHover: StyleParamType.Color;
  linkFont: StyleParamType.Font;
  linkColor: StyleParamType.Color;
  linkColorHover: StyleParamType.Color;
};

const paywallStylesParams = createStylesParams<IStylesParams>({
  contentAlignment: {
    type: StyleParamType.Number,
    getDefaultValue: () => Alignment.CENTER,
    metadata: {
      type: StyleType.textAlign,
      ai: {
        description:
          'Content alignment for all elements in the paywall widget. You can use this to change the alignment to LEFT - 0, CENTER - 1, or RIGHT - 2.',
      },
      defaultValue: {
        type: 'static',
        value: 'center',
      },
    },
  },
  backgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Background color of the paywall widget.',
      },
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  borderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description: 'Border color of the paywall container.',
      },
      defaultValue: {
        type: 'static',
        preset: CssColor.WstSystemLine1Color,
        opacity: 0,
      },
    },
  },
  borderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description: 'Border width of the paywall container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  borderTopLeftRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      ai: {
        description: 'Top left corner radius of the paywall container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  borderTopRightRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      ai: {
        description: 'Top right corner radius of the paywall container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  borderBottomRightRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      ai: {
        description: 'Bottom right corner radius of the paywall container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  borderBottomLeftRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      ai: {
        description: 'Bottom left corner radius of the paywall container in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-XL'),
  },
  titleMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 28,
    metadata: {
      type: StyleType.fontSize,
      ai: {
        description: 'Font size for the paywall title on mobile devices.',
      },
      defaultValue: {
        type: 'static',
        value: 28,
      },
    },
  },
  titleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color for the paywall title.',
      },
      defaultValue: wixColor(CssColor.WstHeading1Color),
    },
  },
  buttonFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  },
  buttonFontColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color for the main CTA button.',
      },
      defaultValue: wixColor(CssColor.WstButtonPrimaryColor),
    },
  },
  buttonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Background color for the main CTA button.',
      },
      defaultValue: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
    },
  },
  buttonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0),
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description: 'Border color for the main CTA button.',
      },
      defaultValue: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
    },
  },
  buttonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      ai: {
        description: 'Border width for the main CTA button in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonBorderTopLeftRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      ai: {
        description: 'Top left corner radius of the button in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonBorderTopRightRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      ai: {
        description: 'Top right corner radius of the button in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonBorderBottomRightRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      ai: {
        description: 'Bottom right corner radius of the button in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonBorderBottomLeftRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      ai: {
        description: 'Bottom left corner radius of the button in pixels.',
      },
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonFontColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonFontColor' });
      return color ?? wixColorParam('color-1')(params);
    },
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color for the main CTA button on hover.',
      },
      defaultValue: {
        type: 'ref',
        ref: 'buttonFontColor',
        fallback: wixColor(CssColor.WstButtonPrimaryColor),
      },
    },
  },
  buttonBackgroundColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonBackgroundColor' });
      return color ?? wixColorParam('color-8')(params);
    },
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Background color for the main CTA button on hover.',
      },
      defaultValue: {
        type: 'ref',
        ref: 'buttonBackgroundColor',
        fallback: wixColor(CssColor.WstButtonPrimaryBackgroundColor),
      },
    },
  },
  buttonBorderColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'buttonBorderColor' });
      return color ?? wixColorParam('color-8', 0)(params);
    },
    metadata: {
      type: StyleType.borderColor,
      ai: {
        description: 'Border color for the main CTA button on hover.',
      },
      defaultValue: {
        type: 'ref',
        ref: 'buttonBorderColor',
        fallback: wixColor(CssColor.WstButtonPrimaryBorderLeftColor),
      },
    },
  },
  linkFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }) => {
      const { value: _, ...font } = wixFontParam('Body-M', { size: 14 })({ textPresets });
      return { ...font } as IWixStyleFont;
    },
  },
  linkColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'page-header-color' });
      return color ?? wixColorParam('color-5')(params);
    },
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color for the back to site link.',
      },
      defaultValue: {
        type: 'ref',
        ref: 'page-header-color',
        fallback: wixColor(CssColor.WstParagraph1Color),
      },
    },
  },
  linkColorHover: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const color = params.getStyleParamValue({ type: StyleParamType.Color, key: 'linkColor' });
      return color ?? wixColorParam('color-5')(params);
    },
    metadata: {
      type: StyleType.color,
      ai: {
        description: 'Text color for the back to site link on hover.',
      },
      defaultValue: {
        type: 'ref',
        ref: 'linkColor',
        fallback: wixColor(CssColor.WstParagraph1Color),
      },
    },
  },
});

export default paywallStylesParams;

function flexAlignmentName(value: Alignment): string {
  const flexAlignmentMap: Record<Alignment, string> = {
    [Alignment.LEFT]: 'flex-start',
    [Alignment.CENTER]: 'center',
    [Alignment.RIGHT]: 'flex-end',
  };
  return flexAlignmentMap[value] ?? 'center';
}

export const customCssVars: CustomCssVarsFn = ({ styleParams }) => {
  const alignmentValue = styleParams.numbers[paywallStylesParams.contentAlignment.key!];

  return {
    contentAlignment: alignmentName(alignmentValue),
    contentFlexAlignment: flexAlignmentName(alignmentValue),
  };
};
